import React, { JSX } from 'react';
import { Provider } from 'components/types/LocationTypes';
import { ClinicalFocus } from 'components/types/ClinicalFocusTypes';
import Constants from '../../../assets/utils/constant';

type LocationProviderProps = {
  provider: Provider;
};

type ClinicalFocusItem = ClinicalFocus & {
  name?: string;
  displayName?: string;
  fields?: {
    Title?: {
      value?: string;
    };
    Name?: {
      value?: string;
    };
    ClinicalFocus?: {
      value?: string;
    };
  };
};

const buildContentHubProviderImageUrl = (
  firstName?: string,
  lastName?: string,
  npi?: string
): string => {
  if (!firstName || !lastName || !npi) {
    return '';
  }

  const cleanValue = (value: string): string =>
    value
      .trim()
      .toLowerCase()
      .replace(/\.\s+/g, '.')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9.-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

  return `${Constants.api.contentHubProviderImageBaseUrl}/${cleanValue(
    firstName
  )}-${cleanValue(lastName)}-${npi}.jpg`;
};

const getClinicalFocusLabel = (
  clinicalFocus: ClinicalFocus
): string => {
  const item = clinicalFocus as ClinicalFocusItem;

  return (
    item.fields?.Title?.value?.trim() ||
    item.fields?.Name?.value?.trim() ||
    item.fields?.ClinicalFocus?.value?.trim() ||
    item.displayName?.trim() ||
    item.name?.trim() ||
    ''
  );
};

const LocationProvider = ({
  provider,
}: LocationProviderProps): JSX.Element => {
  const fullName = provider.FullName?.value?.trim() || '';
  const degrees = provider.Degrees?.value?.trim() || '';

  const nameAndCredentials = degrees
    ? `${fullName}, ${degrees}`
    : fullName;

  const providerImageUrl = buildContentHubProviderImageUrl(
    provider.FirstName?.value,
    provider.LastName?.value,
    provider.NPI?.value
  );

  const fallbackImage =
    provider.Gender?.value?.toLowerCase() === 'male'
      ? Constants.fallbackImages.providerMale
      : Constants.fallbackImages.providerFemale;
  
  const specialtyLabels = (
    provider.Specialty?.value || ''
  )
    .split('|')
    .map(specialty => specialty.trim())
    .filter(
      (specialty, index, specialties) =>
        Boolean(specialty) &&
        specialties.indexOf(specialty) === index
    );

  const clinicalFocusLabels = (
    provider.ClinicalFocus?.value || []
  )
    .map(getClinicalFocusLabel)
    .filter(
      (label, index, labels) =>
        Boolean(label) && labels.indexOf(label) === index
    );

  const providerUrl = `/providers/${
    provider.name || ''
  }-${provider.ExternalID?.value || ''}`.toLowerCase();

  return (
    <li className="provider-card">
      <a
        href={providerUrl}
        className="track-link-click"
      >
        <div className="provider-img">
          <img
            src={providerImageUrl || fallbackImage}
            alt={nameAndCredentials}
            onError={event => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = fallbackImage;
            }}
          />
        </div>

        <div className="provider-information">
          <span className="provider-name btn-tertiary">
            {nameAndCredentials}
          </span>

          {specialtyLabels.length > 0 && (
            <p className="provider-specialty">
              <span>Specialty:</span>{' '}
              {specialtyLabels.join(', ')}
            </p>
          )}

          {clinicalFocusLabels.length > 0 && (
            <p className="provider-clinical-focus">
              <span>Clinical focus:</span>{' '}
              {clinicalFocusLabels.join(', ')}
            </p>
          )}
        </div>
      </a>
    </li>
  );
};

export default LocationProvider;